import { Trans } from '@lingui/macro'
import { CalendarTodayOutlined, CheckOutlined, ClearOutlined } from '@mui/icons-material'
import { Box, Grid, TextField, Tooltip, Typography } from '@mui/material'
import { PickersDay } from '@mui/x-date-pickers'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { dateFormat as defaultDateFormat } from 'app/appSettings'
import { useTabFocusDetection } from 'app/utils/useTabFocus'
import { isNil } from 'lodash'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { ErrorLabel } from '../forms/ErrorLabel'

/**
 * Extended Material UI DatePicker that handles portal localization and overriding theme color. Is recommended to use over regular <DatePicker /> component.
 * @category Common components
 * @subcategory MUI extended
 * @component
 * @returns {JSX.Element}
 * @property {color} color Focus color of the field
 * @property {function} handleClose Callback fired on inputs onClose event
 * @property {function} handleOpen Callback fired on inputs onOpen event
 */
const CustomDatePicker = ({
  value,
  touched = false,
  disabled = false,
  style,
  ...props
}) => {
  const minDate = props.minDate
  const maxDate = props.maxDate
  const handleClose = props.handleClose
  const handleOpen = props.handleOpen

  const [open, setOpen] = useState(false)
  const [alreadyOpen, setAlreadyOpen] = useState(false)
  const textFieldRef = useRef(null)
  const dateFormat = props?.dateFormat || defaultDateFormat

  useTabFocusDetection(
    textFieldRef?.current?.firstChild?.firstChild,
    (element) => {
      setOpen(true)
      element.focus()
      const length = element.value.length
      if (element.setSelectionRange) {
        element.setSelectionRange(length, length)
      }
    },
    (element) => {
      setOpen(false)
    }
  )

  const [isValidWhenOpen, setIsValidWhenOpen] = useState(moment.utc(value, dateFormat, true)?.isValid())

  // value to determine month when open dialog
  const _valueDate = isNil(value)
    ? moment.utc()
    : moment.utc(value)?.isValid() ? moment.utc(value) : moment.utc()
  const month = (minDate && (_valueDate < moment.utc(minDate)))
    ? moment.utc(minDate)
    : (maxDate && (_valueDate > moment.utc(maxDate)))
        ? moment.utc(maxDate)
        : _valueDate.isValid() ? _valueDate : moment.utc()
  const isDiffMonth = month.month() !== _valueDate.month()
  const isDiffYear = month.year() !== _valueDate.year()

  const onOpen = () => {
    if (disabled) {
      return
    }
    if (!isValidWhenOpen && props.onChange) {
      props.onChange(null, 'Invalid date')
    }
    setOpen(true)
    if (handleOpen) {
      handleOpen()
    }
  }

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        textFieldRef.current.firstChild.firstChild.focus()
      }, 50)
    }
  }, [open])

  const onClose = (e) => {
    setOpen(false)
    setAlreadyOpen(false)
    setTimeout(() => {
      textFieldRef.current.firstChild.firstChild.blur()
    }, 50)
    if (handleClose) {
      handleClose()
    }
  }

  const placeholderValue = moment.utc(value, dateFormat, true)?.isValid()
    ? moment.utc(value).format(dateFormat)
    : dateFormat

  if (props.readOnly) {
    return (
      <CustomDatePickerReadOnly
        value={value}
        {...props}
      />
    )
  }

  return (
    <Box sx={{ width: '100%', ...style }}>
      {props.label && (
        <Box
          sx={{
            marginBottom: '16px',
            fontSize: '16px',
            lineHeight: '18px',
            fontWeight: 700,
            color: '#17374F'
          }}
        >
          {props.label}
          {props.required && <span> *</span>}
        </Box>
      )}
      <DatePicker
        {...props}
        views={props?.views || ['year', 'month', 'day']}
        defaultCalendarMonth={month}
        open={open}
        label={null}
        onOpen={onOpen}
        onClose={onClose}
        autoFocus={false}
        inputFormat={props?.inputFormat || dateFormat}
        minDate={minDate ? moment.utc(minDate) : null}
        maxDate={maxDate ? moment.utc(maxDate) : null}
        value={open && !alreadyOpen && (isDiffMonth || isDiffYear)
          ? null
          : value ? moment.utc(value, dateFormat, true) : null}
        disabled={disabled}
        onChange={(e, keyboard) => {
          if (e?.target) {
            return
          }
          const parsedValue = (!e && !keyboard)
            ? null
            : moment.utc(keyboard || e, dateFormat, true)
          const isValid = parsedValue?.isValid()
          if (!isValid) {
            return
          }
          setAlreadyOpen(true)
          setIsValidWhenOpen(isValid)
          if (props?.onChange) {
            props.onChange(parsedValue, keyboard)
          }
        }}
        renderInput={(params) => (
          <TextField
            {...props}
            {...params}
            style={{ width: '100%' }}
            disabled={disabled}
            onClick={(e) => {
              if (disabled) return
              if (open) {
                e.stopPropagation()
                return
              }
              if (!open) {
                setOpen(true)
              }
            }}
            helperText={props.error ? <ErrorLabel label={props.error} /> : params.helperText}
            ref={textFieldRef}
            inputProps={{
              ...params.inputProps,
              placeholder: placeholderValue
            }}
            InputProps={{
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {touched && !disabled && (
                    <Box>
                      {
                        !props.error
                          ? (
                            <CheckOutlined
                              sx={{
                                fontWeight: 700,
                                fontSize: '24px',
                                color: 'success.main',
                                marginRight: '2px',
                                marginTop: '4px'
                              }}
                            />
                            )
                          : (
                            <ClearOutlined
                              sx={{
                                color: 'error.main',
                                fontWeight: 700,
                                fontSize: '24px',
                                marginRight: '2px',
                                marginTop: '6px'
                              }}
                            />
                            )
                      }
                    </Box>
                  )}
                  <Tooltip
                    title={<Trans>Calendar</Trans>}
                    sx={{
                      color: '#17374F'
                    }}
                    disableFocusListener={disabled}
                    disableHoverListener={disabled}
                    disableTouchListener={disabled}
                    disableInteractive={disabled}
                  >
                    <Box
                      sx={{
                        height: '40px',
                        width: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: disabled ? 'cursor' : 'pointer',
                        marginRight: '-2px',
                        '&:hover': {
                          backgroundColor: disabled ? 'transparent' : '#17374F1A',
                          '& .MuiSvgIcon-root': {
                            color: disabled ? 'rgba(0, 0, 0, 0.54) !important' : '#17374F'
                          }
                        }
                      }}
                      onClick={() => {
                        if (disabled) return
                        setOpen(open => !open)
                        setAlreadyOpen(alreadyOpen => !alreadyOpen)
                        if (!open) {
                          setTimeout(() => {
                            textFieldRef.current.firstChild.firstChild.focus()
                          }, 0)
                        }
                      }}
                    >
                      <CalendarTodayOutlined
                        sx={{
                          height: '22px',
                          width: '22px',
                          color: '#0000008A'
                        }}
                      />
                    </Box>
                  </Tooltip>
                </Box>
              )
            }}
            sx={{
              '& .MuiInputBase-root': {
                paddingRight: '4px !important',
                backgroundColor: disabled ? '#17374F1A' : 'transparent'
              },
              '& .MuiInputBase-input': {
                lineHeight: '20px !important',
                paddingTop: '16.5px',
                paddingBottom: '16.5px',
                paddingLeft: '14px',
                '&::placeholder': {
                  color: 'rgba(0, 0, 0, 0.87) !important',
                  opacity: '1 !important'
                }
                // WebkitTextFillColor: 'rgba(0, 0, 0, 0.87) !important'
              },
              '& .MuiOutlinedInput-root': {
                lineHeight: '20px !important',
                minHeight: '40px !important',
                color: 'rgba(0, 0, 0, 0.87)',
                paddingTop: '0px',
                paddingBottom: '0px',
                paddingLeft: '0px',
                paddingRight: '10px',
                '& fieldset': {
                  borderColor: 'rgba(0, 0, 0, 0.87) !important'
                },
                '&:hover fieldset': {
                  borderColor: disabled ? 'rgba(0, 0, 0, 0.40) !important' : '#17374F !important'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#17374F !important'
                }
              },
              '& .MuiTouchRipple-root': {
                backgroundColor: '#17374F1A',
                borderRadius: 0
              }
            }}
          />
        )}
        dayOfWeekFormatter={(day) => day}
        renderDay={(day, selectedDay, dayProps) => {
          const isSelected = day.isSame(selectedDay[0], 'day')
          const isValue = day.isSame(moment.utc(value), 'day')
          const isDisabled = dayProps.disabled
          return (
            <PickersDay
              day={day}
              {...dayProps}
              sx={{
                backgroundColor: isDisabled
                  ? '#eeefe6'
                  : isSelected
                    ? 'transparent'
                    : isValue
                      ? '#17374F'
                      : 'white',
                borderRadius: '50%',
                ...(isValue && {
                  color: '#eeefe6 !important'
                })
              }}
            />
          )
        }}
      />
    </Box>
  )
}

const CustomDatePickerReadOnly = ({ value, ...props }) => {
  const dateFormat = props?.dateFormat || defaultDateFormat

  const formattedValue = value
    ? moment.utc(value, dateFormat, true).isValid()
      ? moment.utc(value, dateFormat, true).format(dateFormat)
      : dateFormat
    : dateFormat

  return (
    <Grid item xs sx={{ textAlign: 'center', ...props?.sx }}>
      {props.label && (
        <Typography
          sx={{
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: 600,
            color: '#17374F',
            marginBottom: '8px !important'
          }}
        >
          {props.label}
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: '16px',
          lineHeight: '18px',
          fontWeight: 500,
          color: '#000000'
        }}
      >
        {formattedValue}
      </Typography>
    </Grid>
  )
}

export default CustomDatePicker
